import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '@/lib/adminApi';
import { artistCategoryNames, artistCategoryOptions } from '@/lib/artistCategories';

const statuses = ['New', 'Contacted', 'Interested', 'Claimed', 'Rejected', 'Needs Follow-up'];
const priorities = ['Hot', 'Warm', 'Cold'];

const emptyForm = {
  artist_name: '',
  instagram_handle: '',
  instagram_url: '',
  category: '',
  category_other: '',
  subcategory: '',
  subcategory_other: '',
  city: '',
  contact_email: '',
  contact_mobile: '',
  source: 'Instagram',
  priority: 'Warm',
  last_contacted_at: '',
  follow_up_at: '',
  notes: '',
  status: 'New',
};

const todayDate = () => new Date().toISOString().slice(0, 10);

const toDateInput = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const leadPayload = (lead, overrides = {}) => ({
  artist_name: lead.artist_name || '',
  instagram_handle: lead.instagram_handle || '',
  instagram_url: lead.instagram_url || '',
  category: lead.category || '',
  subcategory: lead.subcategory || '',
  city: lead.city || '',
  contact_email: lead.contact_email || '',
  contact_mobile: lead.contact_mobile || '',
  source: lead.source || 'Instagram',
  priority: lead.priority || 'Warm',
  last_contacted_at: toDateInput(lead.last_contacted_at),
  follow_up_at: toDateInput(lead.follow_up_at),
  notes: lead.notes || '',
  status: lead.status || 'New',
  ...overrides,
});

const resolveCategoryValue = (value) => {
  if (!value) return { category: '', category_other: '' };
  return artistCategoryOptions[value]
    ? { category: value, category_other: '' }
    : { category: 'Other', category_other: value };
};

const resolveSubcategoryValue = (category, value) => {
  if (!value) return { subcategory: '', subcategory_other: '' };
  return artistCategoryOptions[category]?.includes(value)
    ? { subcategory: value, subcategory_other: '' }
    : { subcategory: 'Other', subcategory_other: value };
};

const leadFormValues = (lead) => {
  const categoryValues = resolveCategoryValue(lead.category || '');
  const subcategoryValues = resolveSubcategoryValue(categoryValues.category, lead.subcategory || '');
  return {
    ...leadPayload(lead),
    ...categoryValues,
    ...subcategoryValues,
  };
};

const buildInviteMessage = (lead) => {
  const artistName = lead.artist_name ? ` ${lead.artist_name}` : '';
  return `Namaste${artistName}! We found your artist profile and loved your work.

KalaManch.Art is building a verified platform for Indian artists to post their profile, portfolio links, photos, and videos so collaborators, event teams, production houses, and clients can discover them.

You can create your free artist profile here:
https://www.kalamanch.art/join

If you are interested, reply "Interested" and we will guide you.

नमस्ते${artistName}! हमने आपका कलाकार प्रोफाइल देखा और आपका काम बहुत पसंद आया।

KalaManch.Art भारतीय कलाकारों के लिए एक verified platform बना रहा है, जहाँ कलाकार अपना profile, portfolio links, photos और videos पोस्ट कर सकते हैं, ताकि collaborators, event teams, production houses और clients उन्हें आसानी से खोज सकें।

आप अपना free artist profile यहाँ बना सकते हैं:
https://www.kalamanch.art/join

अगर आप interested हैं, तो "Interested" reply करें। हम आपको guide कर देंगे।

Team Kalamanch.art
me@kalamanch.art`;
};

const buildFollowUpMessage = (lead) => {
  const artistName = lead.artist_name ? ` ${lead.artist_name}` : '';
  return `Namaste${artistName}, just following up from KalaManch.Art.

If you would like your artist profile listed for collaborations, events, production houses, and clients, you can join here:
https://www.kalamanch.art/join

Namaste${artistName}, KalaManch.Art ki taraf se chhota sa follow-up.

Agar aap collaborations, events, production houses aur clients ke liye apna artist profile list karna chahte hain, to yahan join kar sakte hain:
https://www.kalamanch.art/join

Team Kalamanch.art
me@kalamanch.art`;
};

const buildProfileHelpMessage = (lead) => {
  const artistName = lead.artist_name ? ` ${lead.artist_name}` : '';
  return `Namaste${artistName}! If you want, we can help you prepare your KalaManch artist profile.

Please keep ready:
1. Artist name
2. City
3. Art category
4. Short bio
5. One portfolio link: Instagram, YouTube, or Spotify
6. Profile photo

Namaste${artistName}! Agar aap chahen to hum aapka KalaManch artist profile prepare karne mein help kar sakte hain.

Please ye details ready rakhen:
1. Artist name
2. City
3. Art category
4. Short bio
5. Ek portfolio link: Instagram, YouTube, ya Spotify
6. Profile photo

Team Kalamanch.art
me@kalamanch.art`;
};

const copyText = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const statusClass = (status) => {
  if (status === 'Claimed') return 'bg-green-100 text-green-700';
  if (status === 'Interested') return 'bg-teal-100 text-teal-700';
  if (status === 'Contacted') return 'bg-purple-100 text-[#4A148C]';
  if (status === 'Rejected') return 'bg-gray-200 text-gray-700';
  if (status === 'Needs Follow-up') return 'bg-yellow-100 text-yellow-800';
  return 'bg-orange-100 text-[#D35400]';
};

const priorityClass = (priority) => {
  if (priority === 'Hot') return 'bg-red-100 text-red-700';
  if (priority === 'Cold') return 'bg-slate-100 text-slate-700';
  return 'bg-amber-100 text-amber-700';
};

const compactButtonClass =
  'rounded-full border px-3 py-1.5 text-xs font-bold transition hover:-translate-y-0.5 hover:shadow-sm';

const followUpLabel = (lead) => {
  const followUp = toDateInput(lead.follow_up_at);
  if (!followUp) return { text: 'No follow-up date', className: 'bg-slate-100 text-slate-600' };
  const today = todayDate();
  if (followUp < today) return { text: `Overdue: ${followUp}`, className: 'bg-red-100 text-red-700' };
  if (followUp === today) return { text: 'Due today', className: 'bg-orange-100 text-[#D35400]' };
  return { text: `Follow up: ${followUp}`, className: 'bg-teal-100 text-teal-700' };
};

const AdminArtistLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [inviteMessageTemplate, setInviteMessageTemplate] = useState('');
  const [statusCounts, setStatusCounts] = useState({});
  const [priorityCounts, setPriorityCounts] = useState({});
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [followUpFilter, setFollowUpFilter] = useState('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const response = await adminApi.get(`/admin/artist-leads${showDeleted ? '?include_deleted=true' : ''}`);
      setLeads(response.data.leads || []);
      setStatusCounts(response.data.status_counts || {});
      setPriorityCounts(response.data.priority_counts || {});
      setSelectedLeadIds([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const loadPage = async () => {
      await loadLeads();
      try {
        const settingsResponse = await adminApi.get('/admin/settings');
        if (mounted) {
          setInviteMessageTemplate(settingsResponse.data.settings?.invite_messages?.artist_message || '');
        }
      } catch (loadError) {
        if (mounted) {
          setInviteMessageTemplate('');
        }
      }
    };
    loadPage();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  const filteredLeads = useMemo(() => {
    const today = todayDate();
    return leads.filter((lead) => {
      const followUp = toDateInput(lead.follow_up_at);
      const matchesStatus = statusFilter === 'all' || (lead.status || 'New') === statusFilter;
      const matchesPriority = priorityFilter === 'all' || (lead.priority || 'Warm') === priorityFilter;
      const matchesFollowUp =
        followUpFilter === 'all' ||
        (followUpFilter === 'due' && followUp && followUp <= today) ||
        (followUpFilter === 'future' && followUp && followUp > today) ||
        (followUpFilter === 'none' && !followUp);
      return matchesStatus && matchesPriority && matchesFollowUp;
    });
  }, [followUpFilter, leads, priorityFilter, statusFilter]);

  const leadSubcategoryOptions = useMemo(() => {
    return Array.from(new Set([...(artistCategoryOptions[formData.category] || []), 'Other']));
  }, [formData.category]);

  const updateField = (field, value) => {
    setFormData((current) => {
      if (field === 'category') {
        return { ...current, category: value, category_other: value === 'Other' ? current.category_other : '', subcategory: '', subcategory_other: '' };
      }
      if (field === 'subcategory') {
        return { ...current, subcategory: value, subcategory_other: value === 'Other' ? current.subcategory_other : '' };
      }
      return { ...current, [field]: value };
    });
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setError('');
  };

  const submitLead = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...formData,
        category: formData.category === 'Other' ? formData.category_other.trim() : formData.category,
        subcategory: formData.subcategory === 'Other' ? formData.subcategory_other.trim() : formData.subcategory,
      };
      delete payload.category_other;
      delete payload.subcategory_other;
      if (editingId) {
        await adminApi.put(`/admin/artist-leads/${editingId}`, payload);
      } else {
        await adminApi.post('/admin/artist-leads', payload);
      }
      resetForm();
      await loadLeads();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to save artist lead.');
    } finally {
      setSaving(false);
    }
  };

  const editLead = (lead) => {
    setEditingId(lead.id);
    setFormData(leadFormValues(lead));
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Move this private artist lead to Deleted Leads?')) return;
    await adminApi.delete(`/admin/artist-leads/${leadId}`);
    await loadLeads();
  };

  const restoreLead = async (leadId) => {
    await adminApi.post(`/admin/artist-leads/${leadId}/restore`);
    await loadLeads();
  };

  const permanentDeleteLead = async (leadId) => {
    if (!window.confirm('Permanently delete this private artist lead? This cannot be undone.')) return;
    await adminApi.delete(`/admin/artist-leads/${leadId}/permanent`);
    await loadLeads();
  };

  const allVisibleLeadsSelected = filteredLeads.length > 0 && filteredLeads.every((lead) => selectedLeadIds.includes(lead.id));

  const toggleLeadSelection = (id, checked) => {
    setSelectedLeadIds((current) => (checked ? Array.from(new Set([...current, id])) : current.filter((item) => item !== id)));
  };

  const toggleAllVisibleLeads = () => {
    setSelectedLeadIds(allVisibleLeadsSelected ? [] : filteredLeads.map((lead) => lead.id).filter(Boolean));
  };

  const handleBulkLeadAction = async (action) => {
    if (selectedLeadIds.length === 0) return;
    const confirmText =
      action === 'permanent_delete'
        ? `Permanently delete ${selectedLeadIds.length} selected artist lead(s)? This cannot be undone.`
        : action === 'restore'
          ? `Undo delete for ${selectedLeadIds.length} selected artist lead(s)?`
          : `Move ${selectedLeadIds.length} selected artist lead(s) to Deleted Leads?`;
    if (!window.confirm(confirmText)) return;
    setBulkBusy(true);
    try {
      await adminApi.post('/admin/bulk/artist-leads', { ids: selectedLeadIds, action });
      await loadLeads();
    } finally {
      setBulkBusy(false);
    }
  };

  const copyTemplate = async (lead, type) => {
    const templates = {
      invite: () => inviteMessageTemplate || buildInviteMessage(lead),
      followup: buildFollowUpMessage,
      help: buildProfileHelpMessage,
    };
    await copyText(templates[type](lead));
    setCopiedId(`${type}-${lead.id}`);
    window.setTimeout(() => setCopiedId(''), 2500);
  };

  const markContacted = async (lead) => {
    await adminApi.post(`/admin/artist-leads/${lead.id}/log`, {
      note: 'Marked contacted from admin follow-up toolkit.',
      status: lead.status === 'New' ? 'Contacted' : lead.status,
      follow_up_at: lead.follow_up_at || '',
    });
    await loadLeads();
  };

  const setNeedsFollowUp = async (lead) => {
    await adminApi.put(`/admin/artist-leads/${lead.id}`, leadPayload(lead, { status: 'Needs Follow-up' }));
    await loadLeads();
  };

  const updateLeadStatus = async (lead, status) => {
    await adminApi.put(`/admin/artist-leads/${lead.id}`, leadPayload(lead, { status }));
    await loadLeads();
  };

  const createDraftProfile = async (lead) => {
    try {
      await adminApi.put(`/admin/artist-leads/${lead.id}`, {
        ...leadPayload(lead),
        status: lead.status === 'New' ? 'Interested' : lead.status,
      });
    } catch (error) {
      // Drafting can continue even if status update fails because no public profile is created here.
    }
    navigate('/admin/artists', { state: { draftLead: lead } });
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#FF6F00]">Growth outreach</p>
        <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Artist lead follow-up toolkit</h2>
            <p className="mt-3 max-w-3xl text-gray-600">
              Keep Instagram artist prospects private, prioritize outreach, schedule follow-ups, and copy respectful bilingual messages. Leads are not public profiles until artists join and consent.
            </p>
          </div>
          <button type="button" onClick={resetForm} className="btn-outline">
            Add New Lead
          </button>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button type="button" onClick={() => setShowDeleted(false)} className={!showDeleted ? 'btn-primary' : 'btn-outline'}>
            Active Leads
          </button>
          <button type="button" onClick={() => setShowDeleted(true)} className={showDeleted ? 'btn-primary' : 'btn-outline'}>
            Deleted Leads
          </button>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
        {statuses.map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
            className={`rounded-3xl border p-4 text-left shadow-sm transition ${statusFilter === status ? 'border-[#FF6F00] bg-orange-50' : 'border-orange-100 bg-white hover:bg-orange-50'}`}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">{status}</p>
            <p className="mt-2 text-2xl font-bold text-gray-900">{statusCounts[status] || 0}</p>
          </button>
        ))}
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="rounded-2xl border border-gray-300 bg-white px-4 py-3">
          <option value="all">All statuses</option>
          {statuses.map((status) => <option key={status}>{status}</option>)}
        </select>
        <select value={priorityFilter} onChange={(event) => setPriorityFilter(event.target.value)} className="rounded-2xl border border-gray-300 bg-white px-4 py-3">
          <option value="all">All priorities</option>
          {priorities.map((priority) => <option key={priority}>{priority}</option>)}
        </select>
        <select value={followUpFilter} onChange={(event) => setFollowUpFilter(event.target.value)} className="rounded-2xl border border-gray-300 bg-white px-4 py-3">
          <option value="all">All follow-ups</option>
          <option value="due">Due or overdue</option>
          <option value="future">Future follow-ups</option>
          <option value="none">No follow-up date</option>
        </select>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {priorities.map((priority) => (
          <div key={priority} className="rounded-3xl border border-orange-100 bg-white p-4 shadow-sm">
            <p className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${priorityClass(priority)}`}>{priority}</p>
            <p className="mt-3 text-2xl font-bold text-gray-900">{priorityCounts[priority] || 0}</p>
            <p className="text-sm text-gray-500">private artist leads</p>
          </div>
        ))}
      </section>

      <form onSubmit={submitLead} className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">{editingId ? 'Edit artist lead' : 'Add artist lead'}</h3>
            <p className="text-gray-600">Instagram handle is required. Everything here stays private to admin.</p>
          </div>
          {editingId && (
            <button type="button" onClick={resetForm} className="btn-outline">
              Cancel Edit
            </button>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <input value={formData.artist_name} onChange={(event) => updateField('artist_name', event.target.value)} placeholder="Artist name" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <input value={formData.instagram_handle} onChange={(event) => updateField('instagram_handle', event.target.value)} placeholder="@instagram_handle or profile URL" className="rounded-2xl border border-gray-300 px-4 py-3" required />
          <select value={formData.status} onChange={(event) => updateField('status', event.target.value)} className="rounded-2xl border border-gray-300 px-4 py-3">
            {statuses.map((status) => <option key={status}>{status}</option>)}
          </select>
          <select value={formData.priority} onChange={(event) => updateField('priority', event.target.value)} className="rounded-2xl border border-gray-300 bg-white px-4 py-3">
            {priorities.map((priority) => <option key={priority}>{priority}</option>)}
          </select>
          <input value={formData.source} onChange={(event) => updateField('source', event.target.value)} placeholder="Lead source, e.g. Instagram" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <input type="date" value={toDateInput(formData.follow_up_at)} onChange={(event) => updateField('follow_up_at', event.target.value)} className="rounded-2xl border border-gray-300 px-4 py-3" title="Follow-up date" />
          <select value={formData.category} onChange={(event) => updateField('category', event.target.value)} className="rounded-2xl border border-gray-300 bg-white px-4 py-3">
            <option value="">Select art category</option>
            {artistCategoryNames.map((category) => <option key={category} value={category}>{category}</option>)}
          </select>
          {formData.category === 'Other' && (
            <input value={formData.category_other} onChange={(event) => updateField('category_other', event.target.value)} placeholder="Write art category name" className="rounded-2xl border border-gray-300 px-4 py-3" />
          )}
          <select value={formData.subcategory} onChange={(event) => updateField('subcategory', event.target.value)} className="rounded-2xl border border-gray-300 bg-white px-4 py-3" disabled={!formData.category}>
            <option value="">{formData.category ? 'Select related art work' : 'Choose category first'}</option>
            {leadSubcategoryOptions.map((subcategory) => <option key={subcategory} value={subcategory}>{subcategory}</option>)}
          </select>
          {formData.subcategory === 'Other' && (
            <input value={formData.subcategory_other} onChange={(event) => updateField('subcategory_other', event.target.value)} placeholder="Write art subcategory / speciality" className="rounded-2xl border border-gray-300 px-4 py-3" />
          )}
          <input value={formData.city} onChange={(event) => updateField('city', event.target.value)} placeholder="City / state" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <input value={formData.contact_email} onChange={(event) => updateField('contact_email', event.target.value)} placeholder="Public business email, if visible" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <input value={formData.contact_mobile} onChange={(event) => updateField('contact_mobile', event.target.value)} placeholder="Public mobile, if visible" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <input value={formData.instagram_url} onChange={(event) => updateField('instagram_url', event.target.value)} placeholder="Instagram URL optional" className="rounded-2xl border border-gray-300 px-4 py-3" />
        </div>

        <textarea value={formData.notes} onChange={(event) => updateField('notes', event.target.value)} placeholder="Private notes: open for collab, good portfolio, village/town focus, products/services, etc." className="mt-4 min-h-[110px] w-full rounded-2xl border border-gray-300 px-4 py-3" />

        {error && <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

        <button type="submit" disabled={saving} className="btn-primary mt-5 disabled:cursor-not-allowed disabled:bg-gray-300">
          {saving ? 'Saving...' : editingId ? 'Save Lead Changes' : 'Create Lead'}
        </button>
      </form>

      <section className="rounded-[2rem] border border-orange-100 bg-white shadow-lg">
        <div className="flex flex-wrap items-center gap-3 border-b border-orange-100 bg-orange-50/50 p-4">
          <button type="button" onClick={toggleAllVisibleLeads} className="btn-outline px-4 py-2 text-sm" disabled={filteredLeads.length === 0}>
            {allVisibleLeadsSelected ? 'Clear all visible' : 'Select all visible'}
          </button>
          {!showDeleted ? (
            <button
              type="button"
              onClick={() => handleBulkLeadAction('delete')}
              disabled={bulkBusy || selectedLeadIds.length === 0}
              className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              Delete selected
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => handleBulkLeadAction('restore')}
                disabled={bulkBusy || selectedLeadIds.length === 0}
                className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
              >
                Undo selected
              </button>
              <button
                type="button"
                onClick={() => handleBulkLeadAction('permanent_delete')}
                disabled={bulkBusy || selectedLeadIds.length === 0}
                className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Permanent delete selected
              </button>
            </>
          )}
          <span className="text-sm font-semibold text-gray-500">{selectedLeadIds.length} lead(s) selected</span>
        </div>
        {loading ? (
          <div className="p-6 text-gray-500">Loading artist leads...</div>
        ) : filteredLeads.length === 0 ? (
          <div className="p-6 text-gray-500">No artist leads match the current filters.</div>
        ) : (
          <div className="divide-y divide-orange-100">
            {filteredLeads.map((lead) => {
              const followUp = followUpLabel(lead);
              return (
              <article key={lead.id} className="grid gap-5 p-5 sm:p-6 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
                <div className="min-w-0 space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedLeadIds.includes(lead.id)}
                      onChange={(event) => toggleLeadSelection(lead.id, event.target.checked)}
                      className="h-4 w-4 accent-orange-600"
                      aria-label="Select artist lead"
                    />
                    <h3 className="mr-1 text-xl font-bold text-gray-900">{lead.artist_name || lead.instagram_handle}</h3>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusClass(lead.status)}`}>{lead.status || 'New'}</span>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${priorityClass(lead.priority || 'Warm')}`}>{lead.priority || 'Warm'}</span>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${followUp.className}`}>{followUp.text}</span>
                    {lead.category && <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-bold text-[#FF6F00]">{lead.category}</span>}
                  </div>
                  <a href={lead.instagram_url} target="_blank" rel="noopener noreferrer" className="inline-block break-all text-sm font-semibold text-pink-600">
                    @{lead.instagram_handle}
                  </a>
                  <div className="grid gap-3 text-sm text-gray-600 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-2xl bg-slate-50 px-3 py-2">
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Source</p>
                      <p className="mt-1 font-semibold text-gray-800">{lead.source || 'Instagram'}</p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-3 py-2">
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Subcategory</p>
                      <p className="mt-1 font-semibold text-gray-800">{lead.subcategory || '-'}</p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-3 py-2">
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">City</p>
                      <p className="mt-1 font-semibold text-gray-800">{lead.city || '-'}</p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-3 py-2">
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Contacted</p>
                      <p className="mt-1 font-semibold text-gray-800">{toDateInput(lead.last_contacted_at) || '-'}</p>
                    </div>
                  </div>
                  {lead.notes && <p className="rounded-2xl bg-orange-50 px-4 py-3 text-sm text-gray-700">{lead.notes}</p>}
                  {lead.outreach_log?.length > 0 && (
                    <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-xs text-gray-600">
                      <p className="font-bold text-gray-800">Latest outreach note</p>
                      <p className="mt-1">{lead.outreach_log[lead.outreach_log.length - 1]?.note}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-3 rounded-3xl border border-slate-100 bg-slate-50/70 p-3">
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => updateLeadStatus(lead, 'New')} className={`${compactButtonClass} border-slate-200 bg-white text-slate-700`}>New</button>
                    <button type="button" onClick={() => markContacted(lead)} className={`${compactButtonClass} border-blue-200 bg-blue-50 text-blue-700`}>Contacted</button>
                    <button type="button" onClick={() => updateLeadStatus(lead, 'Interested')} className={`${compactButtonClass} border-green-200 bg-green-50 text-green-700`}>Interested</button>
                    <button type="button" onClick={() => setNeedsFollowUp(lead)} className={`${compactButtonClass} border-yellow-200 bg-yellow-50 text-yellow-800`}>Follow-up</button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => createDraftProfile(lead)} className={`${compactButtonClass} border-teal-200 bg-teal-50 text-teal-700`}>
                    Create Draft Profile
                  </button>
                  <button type="button" onClick={() => copyTemplate(lead, 'invite')} className={`${compactButtonClass} border-orange-500 bg-[#FF6F00] text-white`}>
                    {copiedId === `invite-${lead.id}` ? 'Copied Invite' : 'Copy Invite'}
                  </button>
                  <button type="button" onClick={() => copyTemplate(lead, 'followup')} className={`${compactButtonClass} border-purple-200 bg-purple-50 text-purple-700`}>
                    {copiedId === `followup-${lead.id}` ? 'Copied Follow-up' : 'Copy Follow-up'}
                  </button>
                  <button type="button" onClick={() => copyTemplate(lead, 'help')} className={`${compactButtonClass} border-amber-200 bg-amber-50 text-amber-700`}>
                    {copiedId === `help-${lead.id}` ? 'Copied Help' : 'Copy Profile Help'}
                  </button>
                  {!showDeleted && <button type="button" onClick={() => editLead(lead)} className={`${compactButtonClass} border-slate-200 bg-white text-slate-700`}>Edit</button>}
                  {showDeleted ? (
                    <>
                      <button type="button" onClick={() => restoreLead(lead.id)} className={`${compactButtonClass} border-emerald-200 bg-white text-emerald-700`}>
                        Undo
                      </button>
                      <button type="button" onClick={() => permanentDeleteLead(lead.id)} className={`${compactButtonClass} border-red-200 bg-white text-red-700`}>
                        Permanent Delete
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => deleteLead(lead.id)} className={`${compactButtonClass} border-red-200 bg-white text-red-600`}>
                      Delete
                    </button>
                  )}
                  </div>
                </div>
              </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminArtistLeads;
